import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import StandardsDetailsPopover from '@/components/shared/StandardsPills/StandardsDetailsPopover';
import StandardsTag from '@/components/shared/StandardsPills/StandardsTag';
import { Standard } from '@/components/shared/StandardsPills/types';
import cn from '@/utils/cn';

const STANDARDS_PILL_LIMIT = 3;

type Props = {
  className?: string;
  /**
   * Keep the group on a single line, dropping pills into the overflow count when space runs out.
   * Off by default, which wraps instead — the Lesson Overview header's existing behavior.
   */
  fitOnOneLine?: boolean;
  heading: string;
  label?: string;
  limit?: number;
  locale: Locale;
  standards: Standard[];
};

const StandardsPillGroup = ({
  className,
  fitOnOneLine = false,
  heading,
  label,
  limit = STANDARDS_PILL_LIMIT,
  locale,
  standards,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const withAliases = standards.filter(standard => standard.alias?.length > 0);
  const maxVisible = Math.min(limit, withAliases.length);
  const [visibleCount, setVisibleCount] = useState(maxVisible);

  // Drop one pill per pass until the row fits. Self-terminating — each pass either fits or
  // decrements, and visibleCount floors at zero.
  useLayoutEffect(() => {
    if (!fitOnOneLine) return;

    const container = containerRef.current;
    if (!container) return;

    if (visibleCount > 0 && container.scrollWidth > container.clientWidth) {
      setVisibleCount(visibleCount - 1);
    }
  });

  // AIDEV-NOTE: observe the PARENT, not the group. The group is a shrinkable flex item, so its own
  // width tracks its pill count — observing it would feed the shrink loop back into itself.
  useEffect(() => {
    if (!fitOnOneLine) return undefined;

    const parent = containerRef.current?.parentElement;
    if (!parent) return undefined;

    const observer = new ResizeObserver(() => setVisibleCount(maxVisible));
    observer.observe(parent);

    return () => observer.disconnect();
  }, [fitOnOneLine, maxVisible]);

  if (withAliases.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'tw-flex tw-items-center tw-bg-white',
        fitOnOneLine
          ? 'tw-flex-nowrap tw-min-w-0 tw-shrink-[999] tw-overflow-hidden'
          : 'tw-flex-wrap tw-justify-end',
        className,
      )}
    >
      {label && (
        <>
          <Icon icon="Target" size={16} />
          <Text className="tw-px-1 tw-shrink-0 tw-font-tci" size="sm" tag="span" weight="medium">{label}:</Text>
        </>
      )}
      {withAliases.slice(0, visibleCount).map(standard => (
        <StandardsTag key={standard.id} alias={standard.alias} className="tw-m-1 tw-shrink-0" />
      ))}
      {withAliases.length > visibleCount && (
        <StandardsTag ariaHidden alias="…" className="tw-m-1 tw-shrink-0" />
      )}
      <StandardsDetailsPopover heading={heading} locale={locale} standards={withAliases} />
    </div>
  );
};

export default StandardsPillGroup;
